// Calibration form helper:
//   - Builds an itemised plain-text 'summary' hidden field with the line items
//     only (qty × label — unit price = subtotal). No grand total — prices are
//     onder voorbehoud per the kalibratie disclaimers, so quoting one in the
//     email creates a wrong-impression risk for both client and admin.
//   - Injects a per-row price tag next to each priceable field's label, so the
//     client sees the unit price (or "Op aanvraag") inline. Mirrors the legacy
//     form clients are used to.
//   - Wizard polish: giant page-number, focus management on Next/Previous,
//     a11y patches to Formie markup gaps.

const formatter = new Intl.NumberFormat('nl-BE', {
  style: 'currency',
  currency: 'EUR',
});

const prefersReducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function init(wrapper) {
  const variant = wrapper.dataset.variant;
  const variantLabel = variant === 'topo-h' ? 'Topo-H' : 'Topo-L';
  const priceMap = window.kalibratiePrices?.[variant];

  if (!priceMap) {
    console.warn('[kalibratie-calc] no price map for variant', variant);
    return;
  }

  const form = wrapper.querySelector('form.fui-form');
  if (!form) return;

  // Set the hidden variant field (populateFormValues doesn't override Hidden defaults).
  const variantInput = form.querySelector('input[name*="[variant]"]');
  if (variantInput) variantInput.value = variantLabel;

  // ----- A11y patches for Formie markup gaps -----
  patchFormieAccessibility(form);

  const summaryField = form.querySelector('[name*="[summary]"]');
  // Some envs also have a parallel `summaryClient` hidden field; keep both
  // in sync with the same items-only payload.
  const summaryClientField = form.querySelector('[name*="[summaryClient]"]');
  const numberInputs = form.querySelectorAll('input[type="number"]');

  // Locale-aware copy for the inline price tag and email summary.
  const lang = (document.documentElement.lang || 'nl').toLowerCase();
  const isFr = lang.startsWith('fr');
  const COPY = {
    onRequest: isFr ? 'Sur demande' : 'Op aanvraag',
    devices: isFr ? 'APPAREILS' : 'TOESTELLEN',
    onRequestHeader: isFr ? 'SUR DEMANDE' : 'OP AANVRAAG',
    // Fallback used when the user submitted without entering any qty — keeps
    // the mail's "Wat heb je aangevraagd?" / "Qu'avez-vous demandé ?" section
    // from rendering as an empty paragraph.
    noItems: isFr
      ? 'Aucune quantité indiquée — nous vous contacterons pour clarifier la demande.'
      : 'Geen aantallen ingevuld — we nemen even contact op om de aanvraag te verduidelijken.',
  };

  // Unit suffix display ("/ stuk", "/ uur", etc.) was dropped at client
  // request — prices read as plain "€ X,XX" everywhere (form labels + email
  // summary). The CMS `priceUnit` field stays in place for future use but
  // doesn't affect rendering.

  // Strip the "Aantal " / "Nombre " prefix from form labels so the email summary
  // reads "2x Theodoliet" not "2x Aantal theodolieten".
  function cleanLabel(raw) {
    if (!raw) return '';
    return raw.replace(/^Aantal\s+/i, '').replace(/^Nombre\s+(de\s+)?/i, '').trim();
  }

  // ----- Visual grouping (Dynamometrische ringen / Dynamisch plaatproeftoestel) -----
  // Wrap each heading row + the qty rows below it in a styled container, so the
  // Meetapparatuur page bundles the families like the legacy portal. Stops at the
  // next heading or at the "extra info" textarea row that follows the group.
  form.querySelectorAll('.kalibratie-group-heading').forEach((headingEl) => {
    const headingRow = headingEl.closest('.fui-row, .fui-page-row');
    if (!headingRow || headingRow.dataset.kalGrouped === 'true') return;

    const rows = [headingRow];
    let next = headingRow.nextElementSibling;
    while (next && (next.classList.contains('fui-row') || next.classList.contains('fui-page-row'))) {
      if (next.querySelector('.kalibratie-group-heading')) break;
      rows.push(next);
      next = next.nextElementSibling;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'kalibratie-group';
    headingRow.parentElement.insertBefore(wrapper, headingRow);
    rows.forEach((r) => wrapper.appendChild(r));
    headingRow.dataset.kalGrouped = 'true';
  });

  // ----- Per-row solo cards for single-variant families -----
  // The client thinks of Slagsondes + Statisch plaatproef as their own
  // families (not a "losse toestellen" bucket), so each leading standalone
  // row gets its OWN card with muted accent. Mirrors the legacy portal's
  // "4 families on the Meetapparatuur page" mental model.
  const firstGroup = form.querySelector('.kalibratie-group');
  if (firstGroup) {
    const soloRows = [];
    for (const el of [...firstGroup.parentElement.children]) {
      if (el === firstGroup) break;
      if (el.classList.contains('fui-row') || el.classList.contains('fui-page-row')) {
        soloRows.push(el);
      }
    }
    soloRows.forEach((row) => {
      const card = document.createElement('div');
      card.className = 'kalibratie-group kalibratie-group--solo';
      row.parentElement.insertBefore(card, row);
      card.appendChild(row);
    });
  }


  const labelByHandle = new Map();
  numberInputs.forEach((input) => {
    const handle = handleFromInput(input);
    if (!handle) return;
    const fieldEl = input.closest('.fui-field, .fui-input-container, .fui-row') || input.parentElement;
    const labelEl = fieldEl?.querySelector('.fui-label') || fieldEl?.querySelector('label');
    labelByHandle.set(handle, cleanLabel(labelEl?.textContent || handle));

    // Inject the per-row price tag.
    if (labelEl && !labelEl.querySelector('.kalibratie-price-tag')) {
      const meta = priceMap[handle];
      if (meta) {
        const tag = document.createElement('span');
        tag.className = 'kalibratie-price-tag';
        if (meta.opAanvraag) {
          tag.dataset.onRequest = 'true';
          tag.textContent = COPY.onRequest;
        } else {
          tag.textContent = formatter.format(meta.price);
        }
        labelEl.appendChild(tag);
      }
    }
  });

  function getQty(handle) {
    const input = form.querySelector(`input[name*="[${handle}]"]`);
    if (!input) return 0;
    const v = parseInt(input.value, 10);
    return Number.isFinite(v) && v > 0 ? v : 0;
  }

  function recalculate() {
    const billed = [];
    const onRequest = [];

    // Form order, not price-entry order: the mail lists devices like the portal does.
    for (const handle of labelByHandle.keys()) {
      const meta = priceMap[handle];
      if (!meta) continue;
      const qty = getQty(handle);
      if (qty <= 0) continue;
      if (!meta.inTotal) continue;

      if (meta.opAanvraag) {
        onRequest.push(`• ${qty}× ${labelByHandle.get(handle) || handle}`);
        continue;
      }

      // Unit price only — no qty × price multiplication in the mail, no
      // "/ stuk" suffix.
      billed.push(`• ${qty}× ${labelByHandle.get(handle) || handle} — ${formatter.format(meta.price)}`);
    }

    const parts = [];
    if (billed.length) {
      parts.push(COPY.devices);
      parts.push(...billed);
    }
    if (onRequest.length) {
      if (parts.length) parts.push('');
      parts.push(COPY.onRequestHeader);
      parts.push(...onRequest);
    }
    const text = parts.length ? parts.join('\n') : COPY.noItems;
    if (summaryField) summaryField.value = text;
    if (summaryClientField) summaryClientField.value = text;
  }

  numberInputs.forEach((input) => {
    input.addEventListener('input', recalculate);
    input.addEventListener('change', recalculate);
  });

  recalculate();

  // Catch the case where Formie pre-fills the qty inputs (session-remember,
  // browser autofill, variant switch, etc.) but the user never touches them —
  // in that case no input/change event fires and the summary field stays at
  // whatever value `recalculate()` produced at init. Re-running on submit
  // makes sure the summary reflects the actual qty values being submitted.
  form.addEventListener('submit', recalculate, true);
  form.addEventListener('formie:submit', recalculate);
  // Also re-run when the wizard advances, so e.g. qty changes on page 0 are
  // visible immediately when the user lands on later pages.
  form.addEventListener('formie:page-change', recalculate);

  // ----- Wizard page-number injector + a11y focus management -----
  const pages = [...form.querySelectorAll('.fui-page')];
  if (pages.length > 1) {
    let lastVisibleId = null;
    let firstRun = true;
    let initialDeepPage = false;

    const updatePageNumber = () => {
      const visiblePage = pages.find((p) => !p.hasAttribute('data-fui-page-hidden'));
      if (!visiblePage) return;
      const pageIndex = parseInt(visiblePage.dataset.index, 10) || 0;

      const heading = visiblePage.querySelector('.fui-page-title');
      if (!heading) return;

      let pageNumberSpan = heading.querySelector('.page-number');
      if (!pageNumberSpan) {
        pageNumberSpan = document.createElement('span');
        pageNumberSpan.className = 'page-number';
        pageNumberSpan.setAttribute('aria-hidden', 'true');
        heading.prepend(pageNumberSpan);
      }
      pageNumberSpan.textContent = String(pageIndex + 1).padStart(2, '0');

      if (firstRun) {
        if (pageIndex > 0) initialDeepPage = true;
      } else if (visiblePage.id !== lastVisibleId) {
        focusHeading(heading, true);
      }
      lastVisibleId = visiblePage.id;
      firstRun = false;
    };

    function focusHeading(heading, smoothScroll) {
      if (!heading.hasAttribute('tabindex')) {
        heading.setAttribute('tabindex', '-1');
      }
      requestAnimationFrame(() => {
        heading.focus({ preventScroll: true });
        wrapper.scrollIntoView({
          behavior: smoothScroll && !prefersReducedMotion ? 'smooth' : 'auto',
          block: 'start',
        });
      });
    }

    updatePageNumber();

    if (initialDeepPage) {
      const visiblePage = pages.find((p) => !p.hasAttribute('data-fui-page-hidden'));
      const heading = visiblePage?.querySelector('.fui-page-title');
      if (heading) focusHeading(heading, false);
    }

    const observer = new MutationObserver(updatePageNumber);
    pages.forEach((page) => {
      observer.observe(page, {
        attributes: true,
        attributeFilter: ['data-fui-page-hidden'],
      });
    });
  }
}

// ---- Patch Formie a11y gaps ----
//   - Progress bar with role=progressbar but no aria-label
//   - Agree (GDPR) field with an empty <label>
//   - Required radio groups missing aria-required at group level
function patchFormieAccessibility(form) {
  const progress = form.querySelector('.fui-progress-bar[role="progressbar"]');
  if (progress && !progress.hasAttribute('aria-label')) {
    const lang = document.documentElement.lang || 'nl';
    progress.setAttribute(
      'aria-label',
      lang.startsWith('fr') ? 'Progression du formulaire' : 'Voortgang formulier',
    );
  }

  form.querySelectorAll('.fui-field[data-field-type="agree"]').forEach((field) => {
    const label = field.querySelector('label.fui-checkbox-label');
    const legend = field.querySelector('legend.fui-legend');
    if (label && legend && !label.textContent.trim()) {
      const clone = legend.cloneNode(true);
      clone.querySelectorAll('[aria-hidden="true"]').forEach((n) => n.remove());
      label.innerHTML = clone.innerHTML;
    }
  });

  form.querySelectorAll('.fui-field-required[data-field-type="radio"]').forEach((field) => {
    const fieldset = field.querySelector('fieldset');
    if (fieldset && !fieldset.hasAttribute('aria-required')) {
      fieldset.setAttribute('aria-required', 'true');
    }
  });
}

function handleFromInput(input) {
  // Formie names are like: fields[qtyTheodoliet] or fields[someGroup][qtyTheodoliet]
  const name = input.getAttribute('name') || '';
  const m = name.match(/\[([^\[\]]+)\]\s*$/);
  return m ? m[1] : null;
}

document.addEventListener('DOMContentLoaded', () => {
  const wrappers = document.querySelectorAll('.kalibratie-calc');
  if (!wrappers.length) return;
  wrappers.forEach(init);
});
